import React from 'react';
import moment from 'moment';
import _ from 'lodash';
import { stringifyUrl } from 'query-string';

import { INVENTORY_COUNT_STATUS } from '../constants/Inventories';

/** *
A large set of utility functions used across our applications
***/

// We use this hash when we want to store dictonaries as keys. For
// example, when we want to match a set of filters to a set of results
// this function is useful
export const hashify_dictionary = (dictionary) => {
  // Convert dictionary into an ordered dict
  let array = _.map(dictionary, (value, key) => [[key, value]]);
  // Sort the dictionary by key values
  array = _.sortBy(array, element => element[0]);
  return String(array);
};


// Returns a react component for how we represent dollars on our site
export const format_currency = (integer) => {
  const number = integer ? (integer / 100).toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,') : '0.00';
  const ser = number.split('.');
  return (
    <span>
      <span style={{ fontSize: '70%' }}>$</span>
      {ser[0]}
      <span style={{ fontSize: '70%' }}>.{ser[1]}</span>
    </span>
  );
};

// Formats dollars in integer format
// Smart decimal is useful for unit pricing with 3+ decimal places
export const format_dollars = ({ integer, include_cents = false } = {}) => {
  const number = integer ? (integer / 100).toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,') : '0.00';
  const ser = number.split('.');
  return include_cents ? `$${ser[0]}.${ser[1]}` : `$${ser[0]}`;
};

export const format_percent = percent => (percent ? `${percent.toFixed(1)}%` : '0.0%');

export const format_unit_price = (value) => {
  const decimals = value > 0.1 ? 2 : 3;
  return `$${value.toFixed(decimals)}`;
};

export const format_phone_number = (phone_number) => {
  const number = String(phone_number);
  return `(${number.substring(0, 3)}) ${number.substring(3, 6)}-${number.substring(6, 10)}`;
};

// Our purchase history dates are returned in the format MM-YYYY so we need to reformat them
// to create a moment object.
export const format_month_date = (date, date_format = null) => {
  const date_style = date_format || 'MMM Do, YYYY';
  const proper_format = `${date.split('-')[1]}-${date.split('-')[0]}-1`;
  const moment_date = moment(proper_format);
  if (moment_date.isValid()) {
    return moment_date.format(date_style);
  }
  return this;
};

// Takes a dictionary of query parameters and converts into a string
export const parameterize = (dict) => {
  const params = [];
  _.each(dict, (value, key) => {
    let uri_value = value;
    if (!_.isNull(value) && !_.isUndefined(value) && value !== '') {
      if (value.constructor === Array) {
        uri_value = uri_value.sort();
        uri_value = uri_value.join(',');
      }
      params.push(`${key}=${encodeURIComponent(uri_value)}`);
    }
  });
  return params.join('&');
};


// Formatting our error messages from the server
export const format_error = (error) => {
  // If the error is a string
  if (typeof error === 'string') {
    return error;
  }

  // Status shouldn't display in the error message
  try {
    const cleaned_error = _.omit(error, ['status', 'code']);
    // If the error is a dictionary
    return _.map(cleaned_error, (value, key) => `${key.capitalize()}: ${value}`).join('\n');
  } catch (err) {
    return 'An error has occurred';
  }
};

// String to color converter
export const string_to_color = (string, opacity = 1) => {
  // Generate a random number from the string
  let hash = 0;
  for (let i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  // We only want colors within the predefined hsl. We want the
  // hue between 80-360 to avoid the light orange and yellows
  // https://www.w3schools.com/colors/colors_hsl.asp
  const hue = (hash % 280) + 80;
  return `hsla(${hue}, 50%, 37%, ${opacity})`;
};

// Sets the meta header based on whether we are in mobile view or regular view
export const setResponsiveHeaders = (value) => {
  const viewport = document.querySelector('meta[name=viewport]');
  if (value) {
    viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0');
    const doc = document.querySelector('body');
    if (doc) {
      doc.classList.add('responsive');
    }
  } else {
    viewport.setAttribute('content', 'width=1100');
    const doc = document.querySelector('body');
    if (doc) {
      doc.classList.remove('responsive');
      document.body.style.zoom = 1.0;
    }
  }
};

// A helper function to make the request, success, and failure calls more
// readable and friendly :)
export const createAPIActions = name => ({
  request: `${name}_REQUEST`,
  success: `${name}_SUCCESS`,
  failure: `${name}_FAILURE`
});

export const currentQueryStringAsObject = () => _.chain(window.location.search) // takes the escaped query string...
  .replace('?', '') // strips leading question mark...
  .split('&') // splits on the chief ampersand token...
  .map(_.partial(_.split, _, '=', 2)) // splits each of those tokens on the = separator, truncating any extraneous...
  .fromPairs() // turns that nested list of tuples into a flattened k: v object...
  .value();

export const getCurrentTZAbbrev = function () {
  /** *
   * Gets the 3 character TZ abbreviation for the client's timezone as the browser knows it.
   *
   * Adapted from https://gist.github.com/redoPop/3915761
   * @type {string}
   */
  const dateString = `${new Date()}`;
  let tzAbbr = (
    // Works for the majority of modern browsers: pulls out the parenthesized part of the DT string
    dateString.match(/\(([^\)]+)\)$/)
    // IE outputs date strings in a different format:
    || dateString.match(/([A-Z]+) [\d]{4}$/)
  );

  if (tzAbbr) {
    // Some browsers uses the long TZ name (e.g., "Central Daylight Time" vs. "CDT") so we exclude lowercase.
    tzAbbr = tzAbbr[1].match(/[A-Z]/g).join('');
  }

  return tzAbbr;
};

export const getEmailRegexMatch = (email) => {
  // email domain regex breakdown:
  //   ^\s*                 Allow for whitespace at beginning
  //   .@                   Match any username characters before and including "@"
  //   ?<domain>            names the group "domain"
  //     ([a-z0-9-]+\.)+      one or more [alphanumeric or hyphen] followed by '.' (ex: "vetcove.", "example.test.")
  //     [a-z]{2,}            2+ letters (typically top-level domains like "com")
  //   \s*$/                Allow for whitespace at end
  const re = /^\s*.+@(?<domain>([a-z0-9-]+\.)+[a-z]{2,})\s*$/i;
  return re.exec(email);
};

// Shared Inventory Count Functions
// Selector Helpers
export const flattenItem = (item) => {
  const values = {
    name: '',
    description: '',
    location_name: item.inventory_location.name,
    category_name: _.get(item, 'inventory_category.name', null),
    category_id: _.get(item, 'inventory_category.id', null),
    total_price: _.toInteger(item.unit_price * item.count || 0),
    unit_types: [],
  };

  if (item.product_item) {
    Object.assign(values, {
      name: item.product_item.name,
      description: item.product_item.description,
      unit_types: item.product_item.unit_types,
    });
  }

  if (item.custom_item) {
    Object.assign(values, {
      name: item.custom_item.name,
      description: item.custom_item.description,
      unit_types: item.custom_item.unit_types,
    });
  }

  if (item.pim_product) {
    Object.assign(values, {
      name: item.pim_product.name,
      description: item.pim_product.description,
      unit_types: item.pim_product.unit_types
    });
  }

  // display name if it exists
  if (item.name) {
    values.name = item.name;
  }

  // String that we'll frontend filter on
  const query = `${values.name} ${values.description}`;

  return { ...item, ...values, query };
};

export const getInventoryCountExportUrl = (exportPath, {
  message = null,
  categoryId = null,
  locationId = null,
} = {}) => stringifyUrl({
  url: exportPath,
  query: _.pickBy({
    location_id: locationId,
    category_id: categoryId,
    has_message: _.isNil(message) ? undefined : String(message),
  }, value => !_.isNil(value) && value !== ''),
});

export const includeItem = (item, filters) => {
  let queryMatch = true;
  let messageMatch = true;
  let categoryMatch = true;
  let locationMatch = true;
  if (filters.query) {
    queryMatch = _.includes(_.toLower(item.query), _.toLower(filters.query));
  }

  if (!_.isNil(filters.message)) {
    messageMatch = !!item.message === filters.message;
  }

  if (filters.category_id) {
    categoryMatch = item.category_id === filters.category_id;
  }

  if (filters.location_id) {
    locationMatch = item.inventory_location_id === filters.location_id;
  }

  return queryMatch && messageMatch && categoryMatch && locationMatch;
};

export const includeCount = (count, filters) => {
  let statusMatch = true;
  let queryMatch = true;

  if (filters.status) {
    statusMatch = count.status === filters.status;
  }

  if (filters.query) {
    queryMatch = _.includes(_.toLower(count.buyer_name), _.toLower(filters.query));
  }

  return statusMatch && queryMatch;
};

export const getCountStatus = (count) => {
  if (count.completed_at) {
    return INVENTORY_COUNT_STATUS.completed.key;
  }

  return INVENTORY_COUNT_STATUS.in_progress.key;
};

// Downloads a Blob
export const downloadBlob = (blob, fileName, a = null) => {
  if (_.isNil(a)) {
    a = document.createElement('a');
  }
  if (window.URL && ('download' in a)) {
    const url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  }
};

export const sortItems = (items, sort) => _.sortBy(items, (item) => {
  switch (sort) {
    case 'total_price':
      return _.get(item, 'total_price', 0) * -1;
    case 'unit_price':
      return _.get(item, 'unit_price', 0) * -1;
    case 'quantity':
      return _.get(item, 'quantity', 0) * -1;
    default:
      return _.get(item, 'name', '');
  }
});
